import type {
  ChartFieldRoleResolution,
  ChartFieldRoleSelection,
  ChartFieldRoleSources,
} from '../../models/chart.types'

import type { BuildCategoricalChartOptionsInput } from './chartBuilder.types'
import { applyFieldRoleSelectionRules, toChartFieldCandidates } from './fieldRoles'

export interface ResolvedChartFields {
  selection: ChartFieldRoleSelection
  role: (key: string) => string[]
}

function fallbackSelection(input: BuildCategoricalChartOptionsInput): ChartFieldRoleSelection {
  return {
    dimensions: [...input.dimensions],
    dimension: input.dimensions.slice(0, 1),
    dimension_x: input.dimensions.slice(0, 1),
    dimension_y: input.dimensions.slice(1, 2),
    dimension_source: input.dimensions.slice(0, 1),
    dimension_target: input.dimensions.slice(1, 2),
    breakdown: input.dimensions.slice(1, 2),
    metrics: [...input.metrics],
    metric: input.metrics.slice(0, 1),
    metric_x: input.metrics.slice(0, 1),
    metric_y: input.metrics.slice(1, 2),
    metric_size: input.metrics.slice(2, 3),
    time_dimension: (input.timeDimensions ?? []).slice(0, 1),
  }
}

function roleSources(input: BuildCategoricalChartOptionsInput): ChartFieldRoleSources {
  return {
    dimensions: toChartFieldCandidates(input.dimensions),
    metrics: toChartFieldCandidates(input.metrics, input.metricLabel),
    timeDimensions: toChartFieldCandidates(input.timeDimensions ?? []),
    targets: input.targets ?? [],
    values: input.values ?? [],
  }
}

function toResolved(resolution: ChartFieldRoleResolution): ResolvedChartFields {
  return {
    selection: resolution.selection,
    role: (key) => [...(resolution.selection[key] ?? [])],
  }
}

export function resolveChartFields(
  input: BuildCategoricalChartOptionsInput,
): ResolvedChartFields | null {
  if (!input.fieldRoles?.length) {
    const selection = fallbackSelection(input)
    return { selection, role: (key) => [...(selection[key] ?? [])] }
  }

  const resolution = applyFieldRoleSelectionRules(
    input.fieldRoles,
    roleSources(input),
    input.fieldValues,
  )
  return resolution.valid ? toResolved(resolution) : null
}

export function firstField(
  fields: ResolvedChartFields,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = fields.role(key)[0]
    if (value) return value
  }
  return undefined
}

export function manyFields(fields: ResolvedChartFields, keys: readonly string[]): string[] {
  for (const key of keys) {
    const value = fields.role(key)
    if (value.length > 0) return value
  }
  return []
}
